// EditMealModal.js - Updated with correct endpoints
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  LinearProgress,
  Stack,
  Paper,
  Slider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Restaurant as MealIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  EmojiEvents as EmojiEventsIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { QK } from '../../../../../../base/qk';
import { enqueueSnackbar } from 'notistack';
import CustomTextArea from '../../../../../CustomForm/CustomTextArea';
import CustomDateTime from '../../../../../CustomForm/CustomDateTime';
import CustomCheckbox from '../../../../../CustomForm/CustomCheckbox';
import MealConsumed from './MealConsumed';
import { MealOptions } from './meals.config';
import dayjs from 'dayjs';
import { getMealByID, updateMeal } from '../../../../../../api/services/Observations/meal';

const EditMealModal = ({ show, handleClose, selectedID }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    type: '',
    notes: '',
    date_time: '',
    handover: false,
    manager: false,
    value: 0,
  });

  const { data: assessment, isLoading } = useQuery(
    [QK.meal, selectedID],
    () => getMealByID(selectedID),
    {
      enabled: !!selectedID,
      onSuccess: (data) => {
        setFormData({
          type: data.type || '',
          notes: data.notes || '',
          date_time: data.date_time || '',
          handover: data.handover || false,
          manager: data.manager || false,
          value: data.value || 0,
        });
      },
    }
  );

  const { mutate: updateAssessmentMutation, isLoading: isUpdating } = useMutation(
    (newData) => updateMeal(selectedID, newData),
    {
      onSuccess: () => {
        enqueueSnackbar("Meal entry updated successfully", { variant: "success" });
        queryClient.invalidateQueries([QK.meals]);
        handleClose();
      },
      onError: (error) => {
        enqueueSnackbar(error?.response?.data?.message || "Failed to update meal entry", { variant: "error" });
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleValueChange = (event, value) => {
    setFormData((prev) => ({
      ...prev,
      value: value,
    }));
  };

  const handleCheckBox = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = () => {
    if (!formData.type) {
      enqueueSnackbar("Please select a meal type.", { variant: "warning" });
      return;
    }
    updateAssessmentMutation(formData);
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 80) return "#22C55E";
    if (percentage >= 60) return "#3B82F6";
    if (percentage >= 40) return "#F59E0B";
    if (percentage >= 20) return "#F97316";
    return "#EF4444";
  };

  const getPercentageLabel = (percentage) => {
    if (percentage >= 80) return "Excellent";
    if (percentage >= 60) return "Good";
    if (percentage >= 40) return "Average";
    if (percentage >= 20) return "Poor";
    return "Very Poor";
  };

  const getPercentageIcon = (percentage) => {
    if (percentage >= 80) return <EmojiEventsIcon fontSize="small" />;
    if (percentage >= 60) return <SentimentSatisfiedAltIcon fontSize="small" />;
    if (percentage >= 40) return <SentimentDissatisfiedIcon fontSize="small" />;
    return <SentimentVeryDissatisfiedIcon fontSize="small" />;
  };

  if (isLoading) {
    return (
      <Dialog open={show} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!assessment) return null;

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MealIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Edit Meal Entry
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Update meal record details
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <FormControl fullWidth size="medium" required>
            <InputLabel>Meal Type *</InputLabel>
            <Select
              name="type"
              value={formData.type || ""}
              onChange={handleChange}
              label="Meal Type *"
              sx={{
                borderRadius: "8px",
                minHeight: "52px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                },
              }}
            >
              {MealOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      bgcolor: alpha(option.color, 0.12),
                      color: option.color,
                    }}
                  >
                    {option.icon}
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {option.label}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <CustomDateTime
            name="date_time"
            label="Date & Time *"
            onChange={handleChange}
            value={formData.date_time || ""}
            required
            format="YYYY-MM-DD HH:mm"
          />

          <CustomTextArea
            label="Notes"
            name="notes"
            value={formData.notes || ""}
            onChange={handleChange}
            rows={2}
            fullWidth
            placeholder="Additional notes..."
          />

          <MealConsumed
            value={formData.value || 0}
            onChange={handleValueChange}
          />

          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <CustomCheckbox
              label="Share with Manager"
              name="manager"
              checked={formData.manager || false}
              onClick={(checked) => handleCheckBox(checked, "manager")}
            />
            <CustomCheckbox
              label="Handover"
              name="handover"
              checked={formData.handover || false}
              onClick={(checked) => handleCheckBox(checked, "handover")}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isUpdating}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isUpdating}
          startIcon={isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 4,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {isUpdating ? "Updating..." : "Update Meal"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMealModal;